import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {BehaviorSubject, combineLatest, Subscription} from 'rxjs';
import {SelectFilterElement} from '../../../../../components/common/select-filter/select-filter.model';
import {PhoneUtils} from '../../../../../shared/utils/phone.utils';
import {PhoneModel} from '../../entity/phone.model';
import {StringUtils} from '../../../../../shared/utils/string.utils';
import {CountryModel} from '../../../localization/country/api/entity/country.model';
import {CountryFindAllService} from '../../../localization/country/api/service/countryFindAll.service';
import {AuthService} from '../../../../auth/api/service/auth.service';
import {PhoneSavePayload} from '../../payload/phoneSave.payload';
import {FormUtils} from '../../../../../shared/utils/form.utils';

@Component({
    selector: 'app-phone-input',
    templateUrl: './phone-input.component.html',
    providers: [
        CountryFindAllService
    ]
})
export class PhoneInputComponent implements OnInit, OnChanges, OnDestroy{
    @Input() parentFormGroup: UntypedFormGroup;
    @Input() initialPayload: PhoneSavePayload;
    @Input() country: CountryModel;

    @Output() dialingCodeChange = new EventEmitter<string>();
    @Output() numberChange = new EventEmitter<string>();


    dialingCodeControl: UntypedFormControl;
    numberControl: UntypedFormControl;
    defaultDialingCode: string;

    countries: BehaviorSubject<SelectFilterElement<string, CountryModel>[]> =
        new BehaviorSubject<SelectFilterElement<string, CountryModel>[]>([]);
    countryValues: CountryModel[] = [];

    subscription: Subscription;

    constructor(private countryFindAllService: CountryFindAllService,
                private formBuilder: UntypedFormBuilder,
                private authService: AuthService) {
        this.dialingCodeControl = this.formBuilder.control(this.defaultDialingCode, );
        this.numberControl = this.formBuilder.control(undefined);

        if (this.numberControl.value) {
            this.onChangePhoneNumber(this.numberControl.value);
        }

        this.dialingCodeControl.valueChanges
            .subscribe(dialingCode => {
                if (StringUtils.isNotEmpty(dialingCode)) {
                    this.dialingCodeChange.emit('+' + dialingCode);
                } else {
                    this.dialingCodeChange.emit(null);
                }
            });

        this.numberControl.valueChanges
            .subscribe(phoneNumber => {
                // console.logByType['this.phoneNumberControl.valueChanges', this.dialingCodeControl]);
                this.onChangePhoneNumber(phoneNumber);
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        const parentFormGroupChange = changes['parentFormGroup'];
        if (parentFormGroupChange && parentFormGroupChange.currentValue && parentFormGroupChange.isFirstChange()) {
            this.enrichFormGroup();
        }

        if (changes['initialPayload'] && this.initialPayload) {
            this.dialingCodeControl.setValue(this.initialPayload.dialingCode);
            this.numberControl.setValue(this.initialPayload.number);
            // console.log(['patch value', this.initialPayload, this.parentFormGroup]);
        }
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    entityLoaded(phone: PhoneModel | PhoneSavePayload, emitEvent = true) {
        if (phone) {
            this.parentFormGroup.patchValue({
                dialingCode: phone.dialingCode,
                number: phone.number
            }, {emitEvent});
        }
    }

    ngOnInit(): void {
        console.log('Phone input on init');
       this.countryFindAllService.findAll();
       combineLatest([
            this.authService.account,
            this.countryFindAllService.entities$,
        ]).subscribe(([account, countries]) => {
            console.log(['Account or countries changed', countries, account]);
            this.countries.next(countries.map(country => new SelectFilterElement(country.dialingCode, country.dialingCode, country)));
            if (!this.defaultDialingCode) {
                let country = this.country;
                if (!country) {
                    country = account?.summary?.country;
                }
                if (country) {
                    this.defaultDialingCode = country.dialingCode;
                    if (! this.dialingCodeControl.value) {
                        this.dialingCodeControl.setValue(this.defaultDialingCode);
                        // console.log('set dialing code',  this.defaultDialingCode);
                    }
                }
            }

        });

    }

    onChangePhoneNumber(phoneNumber: string) {
        this.selectDialingCodePhoneNumberCountry(phoneNumber);
        if (this.numberChange) {
            this.numberChange.emit(phoneNumber);
        }
    }

    selectDialingCodePhoneNumberCountry(phoneNumber: string) {
        const phoneNumberCountries: CountryModel[] = this.countryValues
            .filter(country => PhoneUtils.phoneNumberFromCountry(phoneNumber, country.dialingCode));
        if (phoneNumberCountries.length === 1) {
            // console.logByType'dialingCodeControl.setValue ' + phoneNumberCountries[0].dialingCode);
            this.dialingCodeControl.setValue(phoneNumberCountries[0].dialingCode);
        }
    }

    filterDialingCode = (criteria: string, country: CountryModel) => {
        return country.dialingCode.startsWith(criteria.replace('+', ''))
            || country.dialingCode.startsWith(criteria.replace('00', ''));
    }

    private enrichFormGroup() {
        this.parentFormGroup.removeControl('dialingCode');
        this.parentFormGroup.removeControl('number');
        this.parentFormGroup.addControl('dialingCode', this.dialingCodeControl);
        this.parentFormGroup.addControl('number', this.numberControl);

        this.numberControl.clearValidators();
        this.dialingCodeControl.clearValidators();

        const validators = [
            Validators.maxLength(15),
            Validators.pattern('^[+]?[(]?[0-9]{3}[)]?[-\\\\s.]?[0-9]{3}[-\\\\s.]?[0-9]{1,10}$')
        ];
        if (FormUtils.isRequiredField(this.parentFormGroup)) {
            validators.push(Validators.required);
            this.dialingCodeControl.setValidators(Validators.required);
        }
        this.numberControl.setValidators(Validators.compose(validators));
    }

    get internationalPhoneNumber(): string {
        return PhoneUtils.convertToInternationalPhoneNumber(this.numberControl.value, this.dialingCodeControl.value);
    }

    get required(): boolean {
        return FormUtils.isRequiredField(this.parentFormGroup);
    }
}
